import React from 'react';

// The list of input fields (by accessionKey) that are handled by this component
const validProfileSpecificFieldAccessionKeys = [
	'doNotEat',
	'Tube 1',
	'Tube 2',
	'Drank Solution',
	'Tube 3',
	'Tube 4',
	'Tube 5',
	'waterIsOk',
	'Tube 6',
	'Tube 7',
	'Tube 8',
	'Tube 9'
];

/**
 * Get the tube name and "When to Collect" values based on an OmsInputField accession key.
 *
 * @param accessionKey The OmsInputField accession key
 * @param showEightTubeDisclaimer Whether to add a disclaimer to 'Tube 2' mentioning old 8-tube packs
 * @return The tube's {description} and {whenToCollect} display values
 */
const getTubeDetails = (accessionKey, showEightTubeDisclaimer) => {
	let tubeTime;

	switch (accessionKey) {
		case 'Tube 1':
			tubeTime = 'Start of collection';
			break;
		case 'Tube 2':
			tubeTime = 'Immediately after Tube 1';
			break;
		case 'Tube 3':
			tubeTime = '20 minutes after the drink';
			break;
		case 'Tube 4':
			tubeTime = `20 minutes after Tube 3`;
			break;
		case 'Tube 5':
			tubeTime = `20 minutes after Tube 4`;
			break;
		case 'Tube 6':
			tubeTime = `30 minutes after Tube 5`;
			break;
		case 'Tube 7':
			tubeTime = `30 minutes after Tube 6`;
			break;
		case 'Tube 8':
			tubeTime = `30 minutes after Tube 7`;
			break;
		case 'Tube 9':
			tubeTime = `30 minutes after Tube 8`;
			break;
		default:
			return null;
	}

	let description = accessionKey;
	if (accessionKey === 'Tube 2' && showEightTubeDisclaimer) {
		description = <span data-disclaimer="**">{description}</span>;
	}

	return {
		description: description,
		whenToCollect: tubeTime
	};
};

/**
 * Requisition 72406515 was the first pack guaranteed to be sent with 9 tubes. For any req onder than
 * that, we need to show a disclaimer that there may be only 8 tubes.
 * Anything without a requsition Id is assummed to be a new pack and we know those should have 9 tubes.
 */
const possibleEightTubeSiboPack = (omsRequisitionId) => {
	return !!omsRequisitionId && omsRequisitionId < 72406515;
};

export default function SiboCollectionFields({ form, omsRequisitionId, onChange, resetFormValidation }) {
	if (!form || !form['Tube 1']) {
		return null;
	}

	return <CollectionTable form={form} onChange={onChange} omsRequisitionId={omsRequisitionId} />;
}

const CollectionTable = ({ form, onChange, omsRequisitionId }) => {
	const showEightTubeDisclaimer = possibleEightTubeSiboPack(omsRequisitionId);
	let hasAnyError = false;

	const tableRows = validProfileSpecificFieldAccessionKeys.map((key, index) => {
		if (key === 'doNotEat') {
			return (
				<tr key={key}>
					<td colSpan={3}>
						<em>Do not eat or drink anything for 1 hour before and during the collection.</em>
					</td>
				</tr>
			);
		}

		if (key === 'waterIsOk') {
			return (
				<tr key={key}>
					<td colSpan={3}>
						<em>Drinking water is okay during the remaining time.</em>
					</td>
				</tr>
			);
		}

		const item = form[key];
		if (!item) {
			return null;
		}

		const hasError = item.valid === false;
		hasAnyError = hasAnyError || hasError;

		if (key === 'Drank Solution') {
			return (
				<tr key={key} className={hasError ? 'text-danger' : null}>
					<td colSpan={2} style={{ textAlign: 'left' }}>
						<strong>
							Immediately after collecting Tube 2 mix lactulose into 8 ounces of water and drink.
						</strong>
					</td>
					<td>
						<input
							type="time"
							name="sample-time"
							pattern="[0-9]{2}:[0-9]{2}[A-Z]{2}"
							value={item.value}
							data-accessionkey={key}
							onChange={onChange}
						/>
					</td>
				</tr>
			);
		}

		const { description, whenToCollect } = getTubeDetails(key, showEightTubeDisclaimer);

		return (
			<tr key={key} className={hasError ? 'text-danger' : null}>
				<td>
					<strong>{description}</strong>
				</td>
				<td>
					<strong>{whenToCollect}</strong>
				</td>
				<td>
					<input
						type="time"
						name="sample-time"
						pattern="[0-9]{2}:[0-9]{2}[A-Z]{2}"
						value={item.value}
						data-accessionkey={key}
						onChange={onChange}
					/>
				</td>
			</tr>
		);
	});

	return (
		<div id="siboTable" className="col-md-12">
			<table className="sibotable">
				<thead>
					<tr>
						<th>Tube #</th>
						<th>When to Complete</th>
						<th>Collection Time</th>
					</tr>
				</thead>
				<tbody>{tableRows}</tbody>
			</table>
			{showEightTubeDisclaimer && (
				<div className="help-block">
					<sup>**</sup> If your collection pack contains only 8 tubes, please leave Tube 2 blank.
				</div>
			)}
			{hasAnyError && (
				<div className="validation-message text-danger pl-10 mt-10">
					<i className="fa fa-warning fa-fw"></i> Please fill out all collection times.
				</div>
			)}
		</div>
	);
};

export { possibleEightTubeSiboPack };
